import React, { useRef, useState } from "react";
import {
  Box,
  Grid,
  IconButton,
  Typography,
  useTheme,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { ItemGrid } from "../../components/layouts/SubHeader/SubHeaderStyled";
import { getSubHeaderBannerBackground } from "../../components/layouts/SubHeader/subHeaderBannerStyles";
import SavedSearchEditSubmenu from "../Search/FilterBar/SavedSearchEditSubmenu";

/**
 * Compact V4 banner for a Saved Search detail page.
 * Title hover reveals rename/delete actions shared with Search.
 * While rename mode is active, show a static title (name moves into the toolbar).
 */
export default function SavedSearchBanner({
  name = "",
  companyCount = 0,
  isDirty = false,
  isEditing = false,
  showForkWarning = false,
  savedSearch = null,
  onEditSearch,
  onDelete,
  /** Optional control rendered to the right of the saved-search title (e.g. V4 Filters). */
  titleAccessory = null,
  /** Optional compact controls rendered alongside the company count. */
  subtitleAccessory = null,
}) {
  const theme = useTheme();
  const editButtonRef = useRef(null);
  const [editMenuOpen, setEditMenuOpen] = useState(false);
  const [titleHovered, setTitleHovered] = useState(false);

  const displayName = isEditing
    ? "Saved Search"
    : isDirty
      ? `${name || "Saved Search"} · editing`
      : name || "Saved Search";
  const countLabel =
    companyCount === 1 ? "1 company" : `${companyCount || 0} companies`;
  const subtitle = isEditing ? "Renaming saved search" : countLabel;
  const showEditIcon = !isEditing && (titleHovered || editMenuOpen);

  const openEditMenu = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setEditMenuOpen(true);
  };

  const closeEditMenu = () => setEditMenuOpen(false);

  return (
    <Grid
      container
      position="relative"
      alignItems="center"
      sx={{
        background: getSubHeaderBannerBackground(theme),
        minHeight: 48,
      }}
      data-testid="saved-search-banner"
    >
      <ItemGrid
        sx={{
          "&::before": { width: 40, height: 48, pointerEvents: "none" },
          "&::after": { width: 72, height: 48, pointerEvents: "none" },
        }}
      >
        <Grid
          container
          alignItems="center"
          justifyContent="space-between"
          wrap="nowrap"
          sx={{
            display: { xs: "grid", md: "flex" },
            gridTemplateColumns: { xs: "minmax(0, 1fr)", md: "none" },
            px: { xs: 2, sm: 5 },
            py: 1.25,
            /* Reserve space for the 72px ::after graphic + 4px right offset + 8px gap. */
            pr: { xs: "88px", sm: 12 },
          }}
        >
          {/* Title + subtitle column — shrinks to let the accessory breathe */}
          <Box sx={{ minWidth: 0, flex: "1 1 auto", overflow: "hidden" }}>
            <Box
              data-testid="saved-search-title-area"
              onMouseEnter={() => setTitleHovered(true)}
              onMouseLeave={() => setTitleHovered(false)}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.5,
                maxWidth: "100%",
                minWidth: 0,
              }}
            >
              <Typography
                component="h1"
                data-testid="saved-search-title"
                sx={{
                  fontWeight: 800,
                  fontSize: 18,
                  lineHeight: "22px",
                  color: isEditing
                    ? theme.palette.text.primary
                    : theme.palette.primary.main,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  minWidth: 0,
                }}
              >
                {displayName}
              </Typography>
              {isEditing ? null : (
                <IconButton
                  ref={editButtonRef}
                  className="saved-search-edit-trigger"
                  size="small"
                  type="button"
                  aria-label={`Rename or delete ${name || "saved search"}`}
                  aria-haspopup="menu"
                  aria-expanded={editMenuOpen ? "true" : undefined}
                  data-testid="saved-search-edit-trigger"
                  onClick={openEditMenu}
                  onMouseDown={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                  }}
                  sx={{
                    ml: 0.25,
                    flex: "0 0 auto",
                    width: 28,
                    height: 28,
                    opacity: showEditIcon ? 1 : 0,
                    pointerEvents: showEditIcon ? "auto" : "none",
                    transition: "opacity 160ms ease",
                    color: theme.palette.action.disabled,
                    "&:hover": {
                      color: theme.palette.text.primary,
                      backgroundColor: theme.palette.action.hover,
                    },
                  }}
                >
                  <EditOutlinedIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
            <Box
              sx={{
                alignItems: "center",
                display: "flex",
                flexWrap: "wrap",
                gap: { xs: 0.5, sm: 1 },
                mt: 0.25,
              }}
            >
              <Typography
                variant="body2"
                color="text.secondary"
                data-testid={
                  isEditing
                    ? "saved-search-editing-description"
                    : "saved-search-company-count"
                }
                sx={{
                  fontSize: 12.5,
                  fontWeight: 500,
                  maxWidth: { xs: "100%", sm: 560 },
                }}
              >
                {subtitle}
              </Typography>
              {subtitleAccessory}
            </Box>
            {showForkWarning ? (
              <Typography
                variant="body2"
                color="text.secondary"
                data-testid="saved-search-fork-warning"
                sx={{ mt: 0.5, fontSize: 12.5, fontWeight: 600 }}
              >
                Editing filters. Saving will create a NEW saved search; this one is unchanged.
              </Typography>
            ) : null}

          </Box>

          {/* Reflows below the subtitle on mobile; stays right-aligned on desktop. */}
          {titleAccessory ? (
            <Box
              data-testid="saved-search-banner-accessory"
              sx={{
                flex: "0 0 auto",
                ml: { xs: 0, md: 1.5 },
                mt: { xs: 1, md: 0 },
                display: "flex",
                alignItems: "center",
              }}
            >
              {titleAccessory}
            </Box>
          ) : null}
        </Grid>
      </ItemGrid>

      <SavedSearchEditSubmenu
        anchorEl={editButtonRef.current}
        open={editMenuOpen}
        savedSearch={savedSearch || { name }}
        onClose={closeEditMenu}
        onRename={(option) => onEditSearch?.(option)}
        onDelete={(option, event) => onDelete?.(option, event)}
      />
    </Grid>
  );
}
